import { UPDropdownItem } from 'components/dropdown';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { getSelectableFunctionsAction } from 'store/actions/employee-function-link-actions';
import { selectableFunctionsSelector } from 'store/selectors/employee-function-link-selectors';
import { IEmployeeSelectableFunctions } from 'types/company/payroll/ISelectableFunctions';

export interface UseSelectableFunctionsResult {
  perEmployee: IEmployeeSelectableFunctions[];
  allowedFunctionIdsFor: (employeeIds: number[]) => number[] | null;
  functionsFor: (employeeIds: number[]) => UPDropdownItem[];
}

// The ids identify a request, not the array they arrive in, which is rebuilt on every render.
const toKey = (employeeIds: number[]): string => [...employeeIds].sort((a, b) => a - b).join(',');

/**
 * Loads the allowed function sets of `candidateEmployeeIds` and narrows dropdown options to them.
 *
 * Every caller asks for the sets of all the employees it could offer, not only the ones already chosen, so
 * the same response answers both "which functions may these employees share" and "which employees still
 * share one".
 */
export const useSelectableFunctions = (
  companyId: number,
  candidateEmployeeIds: number[],
  functions: UPDropdownItem[],
): UseSelectableFunctionsResult => {
  const dispatch = useAppDispatch();
  const { perEmployee } = useAppSelector(selectableFunctionsSelector);
  const candidatesKey = toKey(candidateEmployeeIds);

  useEffect(() => {
    if (!companyId || candidatesKey === '') return;
    dispatch(getSelectableFunctionsAction({ companyId, employeeIds: candidatesKey.split(',').map(toNumber) }));
  }, [companyId, dispatch, candidatesKey]);

  const allowedFunctionIdsFor = useCallback(
    (employeeIds: number[]): number[] | null => {
      const sets = employeeIds.map(id => perEmployee.find(entry => entry.employeeId === id));
      if (sets.length === 0 || sets.some(set => !set)) return null;
      return sets.reduce(
        (common, set) => common.filter(id => set.functionIds.includes(id)),
        sets[0].functionIds.slice(),
      );
    },
    [perEmployee],
  );

  const functionsFor = useCallback(
    (employeeIds: number[]): UPDropdownItem[] => {
      const allowed = allowedFunctionIdsFor(employeeIds);
      if (allowed === null) return functions;
      return functions.filter(item => allowed.includes(toNumber(item.value)));
    },
    [allowedFunctionIdsFor, functions],
  );

  return useMemo(
    () => ({ perEmployee, allowedFunctionIdsFor, functionsFor }),
    [perEmployee, allowedFunctionIdsFor, functionsFor],
  );
};

export const withStoredFunction = (
  options: UPDropdownItem[],
  allFunctions: UPDropdownItem[],
  storedFunctionId: number,
): UPDropdownItem[] => {
  if (!storedFunctionId || options.some(item => toNumber(item.value) === storedFunctionId)) return options;
  const stored = allFunctions.find(item => toNumber(item.value) === storedFunctionId);
  return stored ? [...options, stored] : options;
};

export default useSelectableFunctions;
